import { lazy, Suspense, useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { MapPinCheck } from "lucide-react";
import AppHeader from "@/components/layout/AppHeader";
import FiltersSection from "@/components/layout/FiltersSection";
import MainContent from "@/components/layout/MainContent";
import CashCollected from "@/components/CashCollected";
import MultipleCashDepositModal from "@/components/MultipleCashDepositModal";
import CollectionProgressPanel from "@/components/collection-progress/CollectionProgressPanel";
import type {
  CollectionProgressSelection,
  CollectionStatusKey,
  WorklistStatus,
} from "@/integrations/api/services/collectionProgressService";
import { getApplicationDetails, getApplicationsFromBackend, getFilterOptions, mapApiResponseToApplication } from '@/integrations/api/client';
import { useRealtimeApplicationUpdates } from '@/hooks/useRealtimeApplicationUpdates';
import { generateMonthOptions, getCurrentEmiMonth, toActivityMonth } from '@/utils/formatters';

const ApplicationDetailsPanel = lazy(() => import("@/components/ApplicationDetailsPanel"));

const PAGE_SIZE = 20;

const ATTENDANCE_TICKER = [
  "Attendance not marked for today",
  "Mark attendance before you start",
  "Today’s attendance is still pending",
];

const STATUS_FILTER_LABELS = new Set([
  'overdue',
  'partiallypaid',
  'paid',
]);

const PROGRESS_STATUS_LABELS: Record<CollectionStatusKey, string> = {
  overdue: 'Overdue',
  partially_paid: 'Partially Paid',
  paid: 'Paid',
};

function isStatusFilterOption(status: string) {
  const key = status.toLowerCase().replace(/[\s_\-()&]/g, '');
  return STATUS_FILTER_LABELS.has(key);
}

const Index = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuth();
  const headerRef = useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = useState(80);
  const isNavigatingRef = useRef(false); // Track if we're handling browser navigation

  // State
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [filterOptions, setFilterOptions] = useState(null);
  const [highlightedCard, setHighlightedCard] = useState<{
    loanId: number;
    repaymentId: number;
    amount?: number;
    paymentDate?: string;
    transactionId?: number;
    activityId?: number | null;
    applicantId?: string;
  } | null>(null);
  const [initialTab, setInitialTab] = useState<string | undefined>(undefined);
  const cashCollectedRef = useRef<{ refresh: () => void } | null>(null);
  const [multiDepositOpen, setMultiDepositOpen] = useState(false);
  const [filters, setFilters] = useState({
    branch: [],
    teamLead: [],
    rm: [],
    sourceTeamLead: [],
    sourceRm: [],
    dealer: [],
    lender: [],
    status: [],
    nachStatus: [],
    repayment: [],
    lastMonthBounce: [],
    ptpDate: [],
    onlyVisitFilter: [],
    vehicleStatus: [],
    dpdBucket: [],
    specialCaseFilter: [],
    loanType: []
  });
  const [ptpWorklist, setPtpWorklist] = useState<WorklistStatus | null>(null);
  const [progressSelection, setProgressSelection] =
    useState<CollectionProgressSelection | null>(null);
  const [selectedEmiMonth, setSelectedEmiMonth] = useState(getCurrentEmiMonth());
  // Always Mon-YY from Jan-25 (e.g. Jan-25, Feb-25) — progress API expects this format
  const emiMonthOptions = useMemo(() => generateMonthOptions(), []);
  const shouldShowAttendanceAlert = user?.attendance_marked_today === false;

  // Initialize realtime updates
  useRealtimeApplicationUpdates({
    applications,
    setApplications,
    onApplicationUpdate: (applicationId, updates) => {
      console.log('🔄 Index: Application updated via realtime:', { applicationId, updates });
      // Update the selected application if it's the one being updated
      if (selectedApplication && selectedApplication.applicant_id === applicationId) {
        setSelectedApplication(prev => ({ ...prev, ...updates }));
      }
    }
  });

  // Fetch filter options on mount
  useEffect(() => {
    console.log('🔍 Index.tsx: Fetching filter options...');
    getFilterOptions()
      .then((options) => {
        console.log('🔍 Index.tsx: Filter options received from API:', options);
        console.log('🔍 Index.tsx: demand_num from API:', options.demand_num);
        console.log('🔍 Index.tsx: ptpDateOptions from API:', options.ptpDateOptions);
        setFilterOptions(options);
      })
      .catch((error) => {
        console.error('❌ Index.tsx: Error fetching filter options:', error);
      });
  }, []);

  // Track previous values to prevent unnecessary API calls
  const prevParamsRef = useRef<string>('');
  const prevSearchTermRef = useRef(searchTerm);
  const progressStatus = useMemo(
    () => progressSelection?.status
      ? [PROGRESS_STATUS_LABELS[progressSelection.status]]
      : filters.status,
    [progressSelection?.status, filters.status],
  );
  const progressBucket = useMemo(
    () => progressSelection?.bucket
      ? [progressSelection.bucket]
      : filters.dpdBucket,
    [progressSelection?.bucket, filters.dpdBucket],
  );
  
  // Memoize filters as a serialized string to prevent unnecessary re-renders
  // This ensures the effect only runs when filter values actually change, not when array references change
  const filtersKey = useMemo(() => {
    return JSON.stringify({
      branch: filters.branch,
      teamLead: filters.teamLead,
      rm: filters.rm,
      sourceTeamLead: filters.sourceTeamLead,
      sourceRm: filters.sourceRm,
      dealer: filters.dealer,
      lender: filters.lender,
      status: progressStatus,
      nachStatus: filters.nachStatus,
      repayment: filters.repayment,
      lastMonthBounce: filters.lastMonthBounce,
      ptpDate: filters.ptpDate,
      onlyVisitFilter: filters.onlyVisitFilter,
      vehicleStatus: filters.vehicleStatus,
      dpdBucket: progressBucket,
      specialCaseFilter: filters.specialCaseFilter,
      loanType: filters.loanType,
      ptpWorklist,
      progressSelection,
      emiMonth: selectedEmiMonth,
    });
  }, [filters.branch, filters.teamLead, filters.rm, filters.sourceTeamLead, filters.sourceRm, filters.dealer, filters.lender, progressStatus, filters.nachStatus, filters.repayment, filters.lastMonthBounce, filters.ptpDate, filters.onlyVisitFilter, filters.vehicleStatus, progressBucket, filters.specialCaseFilter, filters.loanType, ptpWorklist, progressSelection, selectedEmiMonth]);

  const progressFilters = useMemo(
    () => ({
      emi_month: selectedEmiMonth,
      branch: filters.branch,
      tl_name: filters.teamLead,
      rm_name: filters.rm,
      source_tl_name: filters.sourceTeamLead,
      source_rm_name: filters.sourceRm,
      dealer: filters.dealer,
      lender: filters.lender,
      search: searchTerm,
      loan_type: filters.loanType,
      status: progressStatus,
      dpd_bucket: progressBucket,
      nach_status: filters.nachStatus,
      ptp_date_filter: filters.ptpDate,
      ptp_worklist: ptpWorklist || undefined,
      demand_num: filters.repayment,
      special_case_filter: filters.specialCaseFilter,
    }),
    [selectedEmiMonth, filters.branch, filters.teamLead, filters.rm, filters.sourceTeamLead, filters.sourceRm, filters.dealer, filters.lender, filters.loanType, searchTerm, progressStatus, progressBucket, filters.nachStatus, filters.ptpDate, ptpWorklist, filters.repayment, filters.specialCaseFilter],
  );
  
  // Fetch applications when filters or page changes
  useEffect(() => {
    const offset = (currentPage - 1) * PAGE_SIZE;
    
    // Prepare filter parameters
    const filterParams = {
      search: searchTerm, // Enable backend search for better performance
      branch: filters.branch,
      teamLead: filters.teamLead,
      rm: filters.rm,
      sourceTeamLead: filters.sourceTeamLead,
      sourceRm: filters.sourceRm,
      dealer: filters.dealer,
      lender: filters.lender,
      status: progressStatus,
      nachStatus: filters.nachStatus,
      repayment: filters.repayment,
      lastMonthBounce: filters.lastMonthBounce,
      ptpDate: filters.ptpDate,
      onlyVisitFilter: filters.onlyVisitFilter,
      vehicleStatus: filters.vehicleStatus,
      dpdBucket: progressBucket,
      specialCaseFilter: filters.specialCaseFilter,
      loanType: filters.loanType,
      ptpWorklist,
      allocationOnly: progressSelection !== null,
      emiMonth: selectedEmiMonth,
    };
    
    // Create a unique key for this request to prevent duplicate calls
    const requestKey = JSON.stringify({
      offset,
      searchTerm,
      filters: filterParams
    });
    
    // Skip if this exact request was already made
    if (prevParamsRef.current === requestKey) {
      console.log('⏭️ Skipping duplicate API call');
      return;
    }
    
    prevParamsRef.current = requestKey;
    const searchChanged = prevSearchTermRef.current !== searchTerm;
    prevSearchTermRef.current = searchTerm;
    setLoading(true);
    if (searchChanged) setSearchLoading(true);
    
    console.log('🚀 Making API call to getApplicationsFromBackend...');
    
    getApplicationsFromBackend(offset, PAGE_SIZE, filterParams)
      .then((data) => {
        const mappedApplications = (data.results || []).map(mapApiResponseToApplication);
        setApplications(mappedApplications); // Set current page applications
        setTotalCount(data.total || 0);
      })
      .catch((error) => {
        console.error('❌ API error:', error);
        setApplications([]);
        prevParamsRef.current = ''; // Reset on error to allow retry
      })
      .finally(() => {
        setLoading(false);
        setSearchLoading(false);
      });
  }, [currentPage, searchTerm, filtersKey]);

  // Sync URL params with application state (handle browser back/forward)
  // Use refs to track state and prevent infinite loops
  const fetchingAppRef = useRef<string | null>(null);
  const lastProcessedAppIdRef = useRef<string | null>(null);
  const lastSelectedAppIdRef = useRef<string | null>(null);
  const applicationsRef = useRef(applications);
  const selectedApplicationRef = useRef(selectedApplication);
  
  // Keep refs in sync
  useEffect(() => {
    applicationsRef.current = applications;
  }, [applications]);
  
  useEffect(() => {
    selectedApplicationRef.current = selectedApplication;
  }, [selectedApplication]);
  
  // Memoize the app ID from URL to prevent unnecessary re-runs
  const appIdFromUrl = useMemo(() => searchParams.get('app'), [searchParams]);
  
  useEffect(() => {
    // Skip URL sync if we're currently navigating from Cash Collected
    // This prevents the effect from interfering with handleNavigateToPayment
    if (isNavigatingRef.current) {
      return; // Let the navigation handler manage the state
    }
    
    const currentSelectedAppId = selectedApplicationRef.current?.applicant_id?.toString() || null;
    
    // Skip if we've already processed this exact state combination
    if (appIdFromUrl === lastProcessedAppIdRef.current && 
        currentSelectedAppId === lastSelectedAppIdRef.current) {
      return; // No change, skip processing
    }
    
    // If URL has app param and it matches current selection, just update refs and return
    if (appIdFromUrl && currentSelectedAppId === appIdFromUrl) {
      lastProcessedAppIdRef.current = appIdFromUrl;
      lastSelectedAppIdRef.current = currentSelectedAppId;
      return; // Already in sync, no action needed
    }
    
    // Update refs to track current state BEFORE any async operations
    lastProcessedAppIdRef.current = appIdFromUrl;
    
    // If URL has app param but state doesn't match, restore from URL
    if (appIdFromUrl && currentSelectedAppId !== appIdFromUrl) {
      // Prevent duplicate fetches
      if (fetchingAppRef.current === appIdFromUrl) {
        console.log('⏭️ Already fetching application from URL, skipping:', appIdFromUrl);
        return; // Already fetching this app
      }
      
      // Find application in current list (use ref to avoid dependency on applications array)
      const appFromList = applicationsRef.current.find(app => app.applicant_id?.toString() === appIdFromUrl);
      
      if (appFromList) {
        console.log('✅ Found application in current list, selecting:', appIdFromUrl);
        isNavigatingRef.current = true; // Mark as browser navigation
        setSelectedApplication(appFromList);
        lastSelectedAppIdRef.current = appIdFromUrl; // Update ref after setting
      } else if (appIdFromUrl) {
        // Application not in current list, fetch it
        console.log('🔍 Application not in list, fetching from API:', appIdFromUrl);
        fetchingAppRef.current = appIdFromUrl;
        isNavigatingRef.current = true;
        getApplicationDetails(appIdFromUrl)
          .then((app) => {
            fetchingAppRef.current = null; // Clear fetching flag
            if (app) {
              // getApplicationDetails already returns a mapped application
              setSelectedApplication(app);
              lastSelectedAppIdRef.current = appIdFromUrl; // Update ref after setting
              console.log('✅ Successfully fetched and selected application:', appIdFromUrl);
            } else {
              // Application not found, remove from URL
              console.log('❌ Application not found, removing from URL:', appIdFromUrl);
              const newSearchParams = new URLSearchParams(searchParams);
              newSearchParams.delete('app');
              setSearchParams(newSearchParams, { replace: true });
              lastProcessedAppIdRef.current = null; // Reset ref
            }
          })
          .catch((error) => {
            fetchingAppRef.current = null; // Clear fetching flag on error
            console.error('❌ Error fetching application from URL:', error);
            // Remove invalid app ID from URL
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.delete('app');
            setSearchParams(newSearchParams, { replace: true });
            lastProcessedAppIdRef.current = null; // Reset ref
          });
      }
    } else if (!appIdFromUrl && selectedApplicationRef.current) {
      // URL doesn't have app param but state has selected application
      // This happens when user navigates back (browser back button or panel back button)
      // Close the panel to sync with URL
      console.log('🔄 URL has no app param but application is selected, closing panel');
      isNavigatingRef.current = true; // Mark as navigation
      setSelectedApplication(null);
      lastSelectedAppIdRef.current = null; // Update ref
      // Reset flag after a brief delay to allow state to update
      setTimeout(() => {
        isNavigatingRef.current = false;
      }, 100);
    }
  }, [appIdFromUrl, setSearchParams, searchParams]); // Use memoized appIdFromUrl instead of searchParams

  // After applications load, check if we need to select an app from URL
  // This prevents calling getApplicationDetails when the app is already in the loaded list
  useEffect(() => {
    if (!loading && appIdFromUrl && !selectedApplicationRef.current) {
      // Applications have finished loading, check if the app from URL is in the list
      const appFromList = applicationsRef.current.find(app => app.applicant_id?.toString() === appIdFromUrl);
      if (appFromList && fetchingAppRef.current !== appIdFromUrl) {
        console.log('✅ Application found in list after loading, selecting:', appIdFromUrl);
        setSelectedApplication(appFromList);
        lastSelectedAppIdRef.current = appIdFromUrl;
        lastProcessedAppIdRef.current = appIdFromUrl;
      }
    }
  }, [loading, appIdFromUrl]); // Only run when loading completes or appIdFromUrl changes

  // Handle browser back/forward navigation
  // Note: We rely on the URL sync effect above to handle popstate changes
  // React Router's useSearchParams will automatically update when popstate fires
  // So we don't need a separate popstate listener - the sync effect handles it

  // Prevent body scrolling when application details panel is open
  useEffect(() => {
    if (selectedApplication) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [selectedApplication]);

  // Measure header height
  useEffect(() => {
    const updateHeaderHeight = () => {
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.offsetHeight);
      }
    };
    
    updateHeaderHeight();
    window.addEventListener('resize', updateHeaderHeight);
    
    return () => window.removeEventListener('resize', updateHeaderHeight);
  }, [shouldShowAttendanceAlert]);

  // Function to filter applications based on selected filters
  const filterApplications = useCallback((apps: any[], currentFilters: any) => {
    return apps.filter(app => {
      // Search is now handled by backend API
      
      // Branch filter
      if (currentFilters.branch && currentFilters.branch.length > 0) {
        if (!currentFilters.branch.includes(app.branch_name)) return false;
      }
      
      // Team Lead filter
      if (currentFilters.teamLead && currentFilters.teamLead.length > 0) {
        if (!currentFilters.teamLead.includes(app.tl_name)) return false;
      }
      
      // RM filter
      if (currentFilters.rm && currentFilters.rm.length > 0) {
        if (!currentFilters.rm.includes(app.rm_name)) return false;
      }
      
      // Dealer filter
      if (currentFilters.dealer && currentFilters.dealer.length > 0) {
        if (!currentFilters.dealer.includes(app.dealer_name)) return false;
      }
      
      // Lender filter
      if (currentFilters.lender && currentFilters.lender.length > 0) {
        if (!currentFilters.lender.includes(app.lender_name)) return false;
      }
      
      // Status filter
      if (currentFilters.status && currentFilters.status.length > 0) {
        if (!currentFilters.status.includes(app.status)) return false;
      }
      
      // Repayment filter (demand_num)
      if (currentFilters.repayment && currentFilters.repayment.length > 0) {
        console.log('🔍 Comparing repayment filter:', {
          filterValues: currentFilters.repayment,
          appDemandNum: app.demand_num,
          appDemandNumType: typeof app.demand_num,
          appDemandNumString: app.demand_num?.toString(),
          includes: currentFilters.repayment.includes(app.demand_num?.toString())
        });
        if (!currentFilters.repayment.includes(app.demand_num?.toString())) return false;
      }
      
      // Vehicle Status filter
      if (currentFilters.vehicleStatus && currentFilters.vehicleStatus.length > 0) {
        if (!currentFilters.vehicleStatus.includes(app.vehicle_status)) return false;
      }

      // Loan Type filter
      if (currentFilters.loanType && currentFilters.loanType.length > 0) {
        if (!currentFilters.loanType.includes(app.loan_type)) return false;
      }
      
      return true;
    });
  }, []);

  // Reset page when filters change (server-side filtering handles the rest)
  useEffect(() => {
    if (Object.values(filters).some(filterArray => filterArray.length > 0)) {
      console.log('🔍 Filters changed, resetting to page 1');
      setCurrentPage(1); // Reset to first page when filters change
    }
  }, [filters]); // Only reset page when filters change, not when data is fetched

  // Applications are already paginated from the server
  const paginatedApplications = applications;

  // Handlers
  const handleFilterChange = (key: string, values: string[]) => {
    console.log('🔧 handleFilterChange called with:', key, values);
    console.log('Filter change - key type:', typeof key);
    console.log('Filter change - values type:', typeof values);
    console.log('Filter change - values content:', values);
    
    // Special handling for filters that should trigger fresh data fetch
    const shouldFetchFreshData = ['ptpDate', 'onlyVisitFilter', 'repayment', 'status', 'nachStatus', 'branch', 'dealer', 'lender', 'rm', 'teamLead', 'sourceRm', 'sourceTeamLead', 'dpdBucket', 'specialCaseFilter', 'loanType'];
    
    if (shouldFetchFreshData.includes(key)) {
      console.log('🔄 Filter change requires fresh data fetch:', key);
      // Clear cached data to force fresh fetch
      setApplications([]);
      setTotalCount(0);
    }
    
    if (key === 'repayment') {
      console.log('🔍 Repayment filter change - values:', values);
      console.log('🔍 Repayment filter change - first value type:', typeof values[0]);
      console.log('🔍 Repayment filter change - first value:', values[0]);
    }
    
    if (key === 'ptpDate') {
      console.log('🔍 PTP Date filter change - values:', values);
      console.log('🔍 PTP Date filter change - will trigger fresh data fetch');
    }
    
    if (key === 'status' || key === 'dpdBucket') {
      setProgressSelection(null);
    }
    setFilters(prev => {
      const newFilters = {
        ...prev,
        [key]: values
      };
      console.log('🔧 Updated filters state:', newFilters);
      return newFilters;
    });
    setCurrentPage(1);
  };
  // Handle application selection with URL sync
  const handleApplicationSelect = useCallback((app) => {
    if (!app) return;
    
    // Clear highlightedCard when manually navigating to a different application
    // This prevents old highlights from persisting when searching/navigating manually
    // Only clear if the new app doesn't match the current highlightedCard
    setHighlightedCard(prev => {
      if (prev && (prev.loanId !== app.loan_id || prev.repaymentId !== app.payment_id)) {
        return null; // Clear if switching to different application
      }
      return prev; // Keep if same application (cash collected navigation)
    });
    
    // Update URL with application ID
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('app', app.applicant_id?.toString() || '');
    setSearchParams(newSearchParams, { replace: false }); // Push to history
    
    // Update state
    setSelectedApplication(app);
    isNavigatingRef.current = false; // Reset flag after state update
  }, [searchParams, setSearchParams]);

  // Handle application close with URL sync
  const handleApplicationClose = useCallback(() => {
    // Remove app from URL
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('app');
    setSearchParams(newSearchParams, { replace: true }); // Replace to avoid adding to history
    
    // Clear highlighted card and initial tab when closing panel
    setHighlightedCard(null);
    setInitialTab(undefined);
    
    // Update state
    setSelectedApplication(null);
    isNavigatingRef.current = false; // Reset flag after state update
  }, [searchParams, setSearchParams]);
  const handleApplicationDeleted = () => {
    setSelectedApplication(null);
    setCurrentPage(1);
  };
  const handleApplicationUpdated = (updatedApp) => {
    setSelectedApplication(updatedApp);
    // Also update the application in the applications list for real-time UI updates
    setApplications(prevApps => 
      prevApps.map(app => 
        app.applicant_id === updatedApp.applicant_id ? updatedApp : app
      )
    );
  };

  // Handle navigation to payment tab from Cash Collected
  const handleNavigateToPayment = useCallback(
    async (
      loanId: number,
      repaymentId: number,
      paymentAmount: number,
      paymentDate: string,
      applicantId: string,
      transactionId: number,
      activityId: number | null,
      applicantName?: string
    ) => {
    try {
        // Mark as navigating to prevent URL sync effect from interfering
        // Set this BEFORE any async operations to prevent race conditions
        isNavigatingRef.current = true;

        const cashMonth = toActivityMonth(paymentDate);
        let appDetails = null;
        try {
          appDetails = await getApplicationDetails(applicantId, cashMonth || undefined, loanId);
        } catch (fetchError) {
          console.warn('Cash collected applicant lookup failed, opening with card data:', fetchError);
        }

        const mappedApp = appDetails
          ? {
              ...appDetails,
              loan_id: appDetails.loan_id ?? loanId,
              emi_month: cashMonth || appDetails.emi_month,
            }
          : {
              id: applicantId,
              applicant_id: applicantId,
              loan_id: loanId,
              payment_id: repaymentId || undefined,
              applicant_name: applicantName || applicantId,
              emi_month: cashMonth || undefined,
              emi_amount: 0,
              principle_due: 0,
              interest_due: 0,
              branch_name: '',
              team_lead: '',
              rm_name: '',
              dealer_name: '',
              lender_name: '',
              lms_status: '',
              user_id: '',
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            };

        setHighlightedCard({
          loanId,
          repaymentId,
          amount: paymentAmount,
          paymentDate,
          transactionId,
          activityId,
          applicantId,
        });
        // Set initial tab to Payment (status key)
        setInitialTab('status');
        
        // IMPORTANT: Set application state FIRST
        setSelectedApplication(mappedApp);
        
        // Update refs AFTER state is set to track current state
        lastSelectedAppIdRef.current = applicantId;
        lastProcessedAppIdRef.current = applicantId;
        
        // Use setTimeout to ensure state update is processed before URL update
        // This prevents the URL sync effect from running before state is set
        setTimeout(() => {
          // Update URL - URL sync effect will see isNavigatingRef=true and skip
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.set('app', applicantId);
          setSearchParams(newSearchParams, { replace: false });
          
          // Reset navigation flag after URL is updated
          // Small delay to ensure URL sync effect has a chance to see the flag
          setTimeout(() => {
            isNavigatingRef.current = false;
          }, 100);
        }, 0);
    } catch (error) {
      console.error('Error navigating to payment:', error);
      toast.error('Failed to open payment page');
      isNavigatingRef.current = false; // Reset flag on error
    }
    },
    [searchParams, setSearchParams]
  );

  // Refetch applications from backend
  const refetchApplications = useCallback(async () => {
    setLoading(true);
    const offset = (currentPage - 1) * PAGE_SIZE;
    
    const filterParams = {
      search: searchTerm,
      branch: filters.branch,
      teamLead: filters.teamLead,
      rm: filters.rm,
      sourceTeamLead: filters.sourceTeamLead,
      sourceRm: filters.sourceRm,
      dealer: filters.dealer,
      lender: filters.lender,
      status: progressStatus,
      nachStatus: filters.nachStatus,
      repayment: filters.repayment,
      lastMonthBounce: filters.lastMonthBounce,
      ptpDate: filters.ptpDate,
      onlyVisitFilter: filters.onlyVisitFilter,
      vehicleStatus: filters.vehicleStatus,
      dpdBucket: progressBucket,
      specialCaseFilter: filters.specialCaseFilter,
      loanType: filters.loanType,
      ptpWorklist,
      allocationOnly: progressSelection !== null,
      emiMonth: selectedEmiMonth,
    };
    
    try {
      const data = await getApplicationsFromBackend(offset, PAGE_SIZE, filterParams);
      const mappedApplications = (data.results || []).map(mapApiResponseToApplication);
      setApplications(mappedApplications);
      setTotalCount(data.total || 0);
      
      // Also update selected application if it's in the list
      if (selectedApplication) {
        const updatedSelectedApp = mappedApplications.find(
          app => app.applicant_id === selectedApplication.applicant_id
        );
        if (updatedSelectedApp) {
          setSelectedApplication(updatedSelectedApp);
        }
      }
    } catch (error) {
      console.error('❌ Error refetching applications:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, filters.branch, filters.teamLead, filters.rm, filters.sourceTeamLead, filters.sourceRm, filters.dealer, filters.lender, progressStatus, filters.nachStatus, filters.repayment, filters.lastMonthBounce, filters.ptpDate, filters.onlyVisitFilter, filters.vehicleStatus, progressBucket, filters.specialCaseFilter, filters.loanType, ptpWorklist, progressSelection, selectedEmiMonth, selectedApplication]);

  // Search handler
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handlePtpWorklistSelect = (status: WorklistStatus) => {
    setPtpWorklist((current) => (current === status ? null : status));
    setCurrentPage(1);
  };

  const handleProgressSelection = (
    selection: CollectionProgressSelection | null,
  ) => {
    setProgressSelection(selection);
    setCurrentPage(1);
  };


  // Map backend filter keys to frontend keys
  const mappedOptions = filterOptions && {
    branches: filterOptions.branches,
    dealers: filterOptions.dealers,
    lenders: filterOptions.lenders,
    statuses: (filterOptions.statuses || []).filter(isStatusFilterOption),
    nachStatusOptions: filterOptions.nach_status_options?.length
      ? filterOptions.nach_status_options
      : ['NACH Clear', 'NACH Failed'],
    ptpDateOptions: ['Today', 'Missed', 'Not Set', 'Later'],
    vehicle_statuses: filterOptions.vehicle_statuses,
    team_leads: filterOptions.team_leads,
    rms: filterOptions.rms,
    source_team_leads: filterOptions.source_team_leads,
    source_rms: filterOptions.source_rms,
    repayments: filterOptions.demand_num, // Map demand_num to repayments
    only_visit_options: filterOptions.only_visit_options || [],
    dpd_buckets: filterOptions.dpd_buckets,
    lastMonthBounce: ['Not paid', 'Paid on time', '1-5 days late', '6-15 days late', '15+ days late'], // Hardcoded options
    specialCaseFilterOptions: filterOptions?.special_case_filter_options || [], // From API
    loanTypeOptions: filterOptions?.loan_type_options || [],
  };

  // Debug logging for filter options
  useEffect(() => {
    if (filterOptions) {
      console.log('🔍 Index.tsx: Filter options from API:', filterOptions);
      console.log('🔍 Index.tsx: Mapped options for frontend:', mappedOptions);
      console.log('🔍 Index.tsx: Repayments (demand_num):', filterOptions.demand_num);
      console.log('🔍 Index.tsx: PTP Date options:', filterOptions.ptpDateOptions);
      console.log('🔍 Index.tsx: Mapped repayments:', mappedOptions?.repayments);
      console.log('🔍 Index.tsx: Mapped ptpDateOptions:', mappedOptions?.ptpDateOptions);
    }
  }, [filterOptions, mappedOptions]);

  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-16 md:pb-0">
      <div ref={headerRef} className="sticky top-0 z-[60] bg-gray-50">
        <AppHeader />
        {shouldShowAttendanceAlert && (
          <div className="w-full md:mx-auto md:max-w-7xl md:px-6 lg:px-8">
            <div
              className="attendance-warn flex h-9 items-center gap-2.5 border-b border-rose-200 bg-rose-50 px-3 sm:gap-3 md:border-x md:px-3"
              role="status"
            >
              <span className="relative z-[1] flex h-2 w-2 shrink-0" aria-hidden="true">
                <span className="absolute inline-flex h-2 w-2 animate-ping rounded-full bg-rose-500 opacity-60" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-rose-600" />
              </span>
              <p className="sr-only">Attendance not marked for today</p>
              <div className="attendance-ticker relative z-[1] min-w-0 flex-1" aria-hidden="true">
                <div className="attendance-ticker-track">
                  {[0, 1].map((copy) => (
                    <span key={copy} className="inline-flex items-center">
                      {ATTENDANCE_TICKER.map((line) => (
                        <span key={`${copy}-${line}`} className="inline-flex items-center gap-3 pr-8 text-[13px] font-medium text-rose-950">
                          {line}
                          <span className="h-1 w-1 shrink-0 rounded-full bg-rose-300" />
                        </span>
                      ))}
                    </span>
                  ))}
                </div>
              </div>
              <Button
                onClick={() => navigate("/attendance")}
                className="relative z-[1] h-7 shrink-0 gap-1 rounded-md bg-rose-700 px-2.5 text-xs font-semibold text-white shadow-none hover:bg-rose-800"
                size="sm"
              >
                <MapPinCheck className="h-3.5 w-3.5" />
                Mark attendance
              </Button>
            </div>
          </div>
        )}
      </div>
      <div className="w-full max-w-7xl mx-auto px-2 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6">
        <div className="space-y-2 sm:space-y-4 md:space-y-6">
          <FiltersSection
            filters={filters}
            availableOptions={mappedOptions}
            onFilterChange={handleFilterChange}
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
            selectedEmiMonth={selectedEmiMonth}
            onEmiMonthChange={setSelectedEmiMonth}
            emiMonthOptions={emiMonthOptions}
            loading={!filterOptions}
            searchLoading={searchLoading}
            totalCount={totalCount}
          />
          <CollectionProgressPanel
            filters={progressFilters}
            paused={!!selectedApplication}
            selectedProgressFilter={progressSelection}
            onProgressFilterSelect={handleProgressSelection}
            selectedPtpWorklist={ptpWorklist}
            onPtpWorklistSelect={handlePtpWorklistSelect}
          />
          <CashCollected 
            onUpdate={refetchApplications} 
            onNavigateToPayment={handleNavigateToPayment}
            onRefReady={(ref) => { cashCollectedRef.current = ref; }}
            filters={filters}
            searchTerm={searchTerm}
            onUploadMultipleReceipt={() => setMultiDepositOpen(true)}
          />
          <MainContent
            applications={paginatedApplications}
            onRowClick={handleApplicationSelect}
            onApplicationDeleted={handleApplicationDeleted}
            selectedApplicationId={selectedApplication?.id}
            currentPage={currentPage}
            totalPages={Math.ceil((totalCount || 1) / PAGE_SIZE)}
            onPageChange={setCurrentPage}
            totalCount={totalCount}
            pageSize={PAGE_SIZE}
          />
        </div>
      </div>
      <MultipleCashDepositModal
        open={multiDepositOpen}
        onOpenChange={setMultiDepositOpen}
        onSuccess={() => {
          cashCollectedRef.current?.refresh();
          refetchApplications();
        }}
      />
      {selectedApplication && (
        <>
          <div 
            className="fixed left-0 right-0 bottom-0 bg-black bg-opacity-50 z-40"
            style={{ top: `${headerHeight}px` }}
            onClick={handleApplicationClose}
          />
          <div 
            className="fixed left-0 right-0 bottom-0 w-full z-50 flex flex-col overflow-hidden bg-gray-50"
            style={{ top: `${headerHeight}px` }}
          >
            <div className="flex min-h-0 flex-1 flex-col py-2 sm:py-4 md:py-4 lg:py-4">
              <div className="mx-auto flex min-h-0 w-full max-w-7xl flex-1 flex-col px-2 sm:px-4 md:px-6 lg:px-8">
                <Suspense fallback={null}>
                <ApplicationDetailsPanel
                  application={selectedApplication}
                  onClose={handleApplicationClose}
                  onSave={handleApplicationUpdated}
                  onDataChanged={refetchApplications}
                  refetchStatusCounts={refetchApplications}
                  initialTab={initialTab}
                  highlightedCard={highlightedCard}
                  onCashInHandChange={() => {
                    // Refresh cash collected list
                    if (cashCollectedRef.current) {
                      cashCollectedRef.current.refresh();
                    }
                    // Refresh applications list to show updated payment data
                    refetchApplications();
                  }}
                />
                </Suspense>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Index;
